import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { MessageId } from "@domain/intl/useTranslation";

import type { AgentContext, AgentDetailItem, AgentStep, ReportLine } from "../runner/types";

const resultTexts: Record<GQL.IReconciliationGroupOutcome, [AgentDetailItem["group"], MessageId]> = {
    [GQL.IReconciliationGroupOutcome.Applied]: ["done", "app.agent.details.reconcile.applied"],
    [GQL.IReconciliationGroupOutcome.AlreadyApplied]: ["untouched", "app.agent.details.reconcile.alreadyApplied"],
    [GQL.IReconciliationGroupOutcome.Changed]: ["needsYou", "app.agent.details.reconcile.changed"],
    [GQL.IReconciliationGroupOutcome.Failed]: ["refused", "app.agent.details.reconcile.failed"],
};

/** The source record with one item per payment record; a group without payments gets one item. */
const groupDetails = (
    ctx: AgentContext,
    source: GQL.IProductKey,
    group: GQL.IReconciliationGroup,
    [itemGroup, textId]: [AgentDetailItem["group"], MessageId],
    withTargets: boolean,
): AgentDetailItem[] => {
    const ref = { kind: "record" as const, id: String(group.sourceKey), product: source };
    const outcome = { textId };
    const payments = withTargets ? group.amounts.filter((a) => String(a.recordKey) !== String(group.sourceKey)) : [];
    if (payments.length === 0) return [{ group: itemGroup, ref, outcome }];
    return payments.map((a) => ({
        group: itemGroup,
        ref,
        target: {
            kind: "record" as const,
            id: String(a.recordKey),
            product: ctx.recordInfo(String(a.recordKey))?.product ?? GQL.IProductKey.Bank,
        },
        outcome,
    }));
};

const skippedTexts: Record<GQL.IReconciliationSkipReason, MessageId> = {
    [GQL.IReconciliationSkipReason.MultipleSources]: "app.agent.details.reconcile.skipped.MULTIPLE_SOURCES",
    [GQL.IReconciliationSkipReason.NoSource]: "app.agent.details.reconcile.skipped.NO_SOURCE",
    [GQL.IReconciliationSkipReason.SumMismatch]: "app.agent.details.reconcile.skipped.SUM_MISMATCH",
};

export const reconcileStep: AgentStep = {
    id: "reconcile",
    async run(ctx, io) {
        // CREDITOR sources are ER records, DEBITOR sources are Deb records
        const sourceOf = (a: GQL.IReconciliationAccount) =>
            a.kind === GQL.IReconciliationAccountKind.Creditor ? GQL.IProductKey.Er : GQL.IProductKey.Deb;
        if (!ctx.canWrite(GQL.IProductKey.Er) && !ctx.canWrite(GQL.IProductKey.Deb)) {
            io.narrateSkipped(GQL.IProductKey.Er);
            io.narrateSkipped(GQL.IProductKey.Deb);
            return { status: "skipped", skipReason: "noRight" };
        }
        const open0 = (await ctx.readReconciliationAccounts()).filter((a) => a.openCount > 0);
        const accounts = open0.filter((a) => ctx.canWrite(sourceOf(a)));
        for (const a of open0) if (!ctx.canWrite(sourceOf(a))) io.narrateSkipped(sourceOf(a));
        if (accounts.length === 0) {
            return open0.length > 0 ? { status: "skipped", skipReason: "noRight" } : { status: "nothing" };
        }

        let skipped = 0;
        let accountsFailed = 0;
        const planned: {
            konto: string;
            source: GQL.IProductKey;
            groups: GQL.IReconciliationGroup[];
            groupIds: string[];
        }[] = [];
        const skippedDetails: AgentDetailItem[] = [];
        for (const account of accounts) {
            if (io.isCancelled()) break;
            try {
                // the konto goes back to the server exactly as the accounts query returned it
                const plan = await withThinking(io, "app.agent.reconcile.thinking", () =>
                    ctx.planReconciliation(account.konto),
                );
                skipped += plan.skipped.length;
                for (const s of plan.skipped) {
                    skippedDetails.push({
                        group: "needsYou",
                        outcome: { textId: skippedTexts[s.reason] },
                    });
                }
                if (plan.groups.length > 0) {
                    planned.push({
                        konto: account.konto,
                        source: sourceOf(account),
                        groups: plan.groups,
                        groupIds: plan.groups.map((g) => String(g.groupId)),
                    });
                }
            } catch (error) {
                console.error(`Agent reconcile failed for account ${account.konto}`, error);
                accountsFailed += 1;
            }
        }
        if (io.isCancelled()) {
            const kept: ReportLine[] = [];
            if (skipped > 0) kept.push({ textId: "app.agent.report.reconcileSkipped", values: { count: skipped } });
            if (accountsFailed > 0) {
                kept.push({ textId: "app.agent.report.reconcileAccountFailed", values: { count: accountsFailed } });
            }
            return { status: "failed", open: kept };
        }

        const details: AgentDetailItem[] = [];
        let applied = 0;
        let changed = 0;
        let failed = 0;
        if (planned.length > 0) {
            const approved = await io.approve({
                textId: "app.agent.reconcile.approve.question",
                values: { accounts: planned.length, groups: planned.reduce((sum, p) => sum + p.groupIds.length, 0) },
                goLabelId: "app.agent.reconcile.approve.go",
                learnMoreId: "app.agent.reconcile.learnMore",
            });
            if (!approved) return { status: "skipped", byUser: true };
            for (const { konto, source, groups, groupIds } of planned) {
                if (io.isCancelled()) break;
                let results: GQL.IReconciliationGroupResult[];
                try {
                    results = await withThinking(io, "app.agent.reconcile.thinking", () =>
                        ctx.applyReconciliation(konto, groupIds),
                    );
                } catch (error) {
                    // a missing Bank or Kasse write right refuses the whole account; the other accounts still run
                    console.error(`Agent reconcile failed for account ${konto}`, error);
                    accountsFailed += 1;
                    const failedText: [AgentDetailItem["group"], MessageId] = [
                        "refused",
                        "app.agent.details.reconcile.accountFailed",
                    ];
                    details.push(...groups.flatMap((g) => groupDetails(ctx, source, g, failedText, false)));
                    continue;
                }
                for (const g of groups) {
                    const result = results.find((r) => String(r.groupId) === String(g.groupId));
                    const mapped = result ? resultTexts[result.outcome] : undefined;
                    if (!result || !mapped) continue;
                    // a group that did not link has no payments to show
                    const linked =
                        result.outcome === GQL.IReconciliationGroupOutcome.Applied ||
                        result.outcome === GQL.IReconciliationGroupOutcome.AlreadyApplied;
                    details.push(...groupDetails(ctx, source, g, mapped, linked));
                }
                const count = (outcome: GQL.IReconciliationGroupOutcome) =>
                    results.filter((r) => r.outcome === outcome).length;
                applied += count(GQL.IReconciliationGroupOutcome.Applied);
                changed += count(GQL.IReconciliationGroupOutcome.Changed);
                failed += count(GQL.IReconciliationGroupOutcome.Failed);
            }
        }

        const summary: ReportLine[] = [];
        const open: ReportLine[] = [];
        if (applied > 0) {
            io.post({ kind: "done", textId: "app.agent.reconcile.done", values: { count: applied }, details });
            summary.push({ textId: "app.agent.report.reconciled", values: { count: applied } });
        }
        if (skipped > 0) open.push({ textId: "app.agent.report.reconcileSkipped", values: { count: skipped } });
        if (changed > 0) open.push({ textId: "app.agent.report.reconcileChanged", values: { count: changed } });
        if (failed > 0) open.push({ textId: "app.agent.report.reconcileFailed", values: { count: failed } });
        if (accountsFailed > 0) {
            open.push({ textId: "app.agent.report.reconcileAccountFailed", values: { count: accountsFailed } });
        }
        details.push(...skippedDetails);
        return { status: summary.length > 0 || open.length > 0 ? "done" : "nothing", summary, open, details };
    },
};
